import React, { createContext, useRef } from "react";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

type AlertType = {
  id: string;
  kind: "success" | "info" | "warn" | "error";
  content: string | JSX.Element;
};

type AddAlertOptionsType = {
  showOnly?: boolean;
  scrollTo?: boolean;
};

type AddAlertType = (
  newAlert: AlertType,
  options?: AddAlertOptionsType,
) => string;

type RemoveAlertType = (id: string) => void;

type ClearAlertsType = () => void;

type AlertContextType = {
  addAlert: AddAlertType;
  alerts: AlertType[];
  removeAlert: RemoveAlertType;
  clearAlerts: ClearAlertsType;
  ref: React.RefObject<HTMLDivElement>;
};

const AlertContext = createContext<AlertContextType>(null);

const AlertProvider = (props) => {
  const [alerts, updateAlerts] = useImmer<AlertType[]>([]);
  const ref = useRef(null);

  return (
    <AlertContext.Provider
      value={{ addAlert, alerts, removeAlert, clearAlerts, ref }}
    >
      {props.children}
    </AlertContext.Provider>
  );

  function addAlert(newAlert: AlertType, options: AddAlertOptionsType = {}) {
    const id = v4();
    updateAlerts((draft) => {
      if (options.showOnly) draft.splice(0);
      draft.push({ ...newAlert, id }); // { kind, content }
    });

    if (options.scrollTo && ref.current) {
      window.scrollTo(0, ref.current.offsetTop);
    }
    return id;
  }

  function removeAlert(id: string) {
    updateAlerts((draft) => {
      const idx = draft.findIndex((alert) => alert.id === id);
      if (idx > -1) draft.splice(idx, 1);
    });
  }

  function clearAlerts() {
    updateAlerts((draft) => {
      draft.splice(0, draft.length);
    });
  }
};

export { AlertContext, AlertProvider };
